import { differenceInCalendarDays } from 'date-fns';
import {
  BookkeepingAdjustmentSourceType,
  BookkeepingAdjustmentType,
  BookkeepingEntrySource,
  CreateBookkeepingAdjustmentPayload,
  CreateBookkeepingEntry,
  CreateBookkeepingTransaction,
} from '~/types/general-bookkeeping';
import { Money } from '~/types/money';
import { ensureDecimalPlaces, multiplyMoney, negateMoney } from '~/utils/money';

export type AdjustmentDescriptionFn = (type: BookkeepingAdjustmentType, variant: 'main' | 'storno') => string;

const ATTIVO_TYPES = new Set<BookkeepingAdjustmentType>([
  BookkeepingAdjustmentType.RISCONTO_ATTIVO,
  BookkeepingAdjustmentType.RATEO_ATTIVO,
  BookkeepingAdjustmentType.FATTURA_DA_EMETTERE,
]);

function transactionPair(
  dec31: Date,
  amount: Money,
  adjustmentAccountId: string,
  originalAccountId: string,
  mainDescription: string,
  stornoDescription: string,
  adjustmentType: BookkeepingAdjustmentType,
): Omit<CreateBookkeepingTransaction, 'companyId'>[] {
  const isAttivo = ATTIVO_TYPES.has(adjustmentType);
  const jan01 = new Date(dec31.getFullYear() + 1, 0, 1);

  // ATTIVO: adjustmentAccount on dare (debit), originalAccount on avere (credit).
  // PASSIVO: originalAccount on dare, adjustmentAccount on avere.
  const [dareId, avereId] = isAttivo
    ? [adjustmentAccountId, originalAccountId]
    : [originalAccountId, adjustmentAccountId];

  const dec31Entries: Omit<CreateBookkeepingEntry, 'lid'>[] = [
    { bookkeepingAccountId: dareId, amount: negateMoney(amount), source: BookkeepingEntrySource.SETTINGS },
    { bookkeepingAccountId: avereId, amount, source: BookkeepingEntrySource.SETTINGS },
  ];
  const jan01Entries: Omit<CreateBookkeepingEntry, 'lid'>[] = [
    { bookkeepingAccountId: avereId, amount: negateMoney(amount), source: BookkeepingEntrySource.SETTINGS },
    { bookkeepingAccountId: dareId, amount, source: BookkeepingEntrySource.SETTINGS },
  ];

  return [
    { date: dec31, description: mainDescription, bookkeepingEntries: dec31Entries, transactionIds: [] },
    { date: jan01, description: stornoDescription, bookkeepingEntries: jan01Entries, transactionIds: [] },
  ];
}

/**
 * Returns true when the entry may require an adjustment entry.
 * Covers both "a cavallo" (period straddles a year boundary) and
 * "fattura da emettere/ricevere" (period entirely in a year prior to registration).
 */
export function requiresAdjustment(
  registrationDate: Date | null,
  competenceStart: Date | null,
  competenceEnd: Date | null,
): boolean {
  if (!registrationDate || !competenceStart || !competenceEnd) return false;
  if (competenceStart.getFullYear() !== competenceEnd.getFullYear()) return true;
  if (registrationDate.getFullYear() > competenceEnd.getFullYear()) return true;
  return false;
}

/**
 * Builds the adjustment payload to embed in a bookkeeping or VAT entry.
 * Generates one transaction pair (31/12 + 01/01 reversal) per year boundary crossed.
 * For "fattura" types a single pair at the end of the competence year is generated.
 * Returns null when no adjustment applies.
 *
 * @param isExpense - true when the original entry is in dare (cost), false when in avere (revenue)
 * @param originalAmount - absolute (positive) amount of the original entry
 */
export function buildAdjustmentPayload(
  adjustmentAccountId: string | null | undefined,
  originalBookkeepingAccountId: string,
  registrationDate: Date,
  competenceStart: Date | null,
  competenceEnd: Date | null,
  isExpense: boolean,
  originalAmount: Money,
  sourceType: BookkeepingAdjustmentSourceType,
  getDescription: AdjustmentDescriptionFn,
): CreateBookkeepingAdjustmentPayload | null {
  if (!adjustmentAccountId || !competenceStart || !competenceEnd) return null;

  const adjustmentType = deriveAdjustmentType(registrationDate, competenceStart, competenceEnd, isExpense);
  if (!adjustmentType) return null;

  const mainDescription = getDescription(adjustmentType, 'main');
  const stornoDescription = getDescription(adjustmentType, 'storno');
  const bookkeepingTransactions: Omit<CreateBookkeepingTransaction, 'companyId'>[] = [];

  if (
    adjustmentType === BookkeepingAdjustmentType.FATTURA_DA_EMETTERE ||
    adjustmentType === BookkeepingAdjustmentType.FATTURA_DA_RICEVERE
  ) {
    const dec31 = new Date(competenceEnd.getFullYear(), 11, 31);
    bookkeepingTransactions.push(
      ...transactionPair(
        dec31,
        originalAmount,
        adjustmentAccountId,
        originalBookkeepingAccountId,
        mainDescription,
        stornoDescription,
        adjustmentType,
      ),
    );
  } else {
    const isRateo =
      adjustmentType === BookkeepingAdjustmentType.RATEO_ATTIVO ||
      adjustmentType === BookkeepingAdjustmentType.RATEO_PASSIVO;
    const totalDays = differenceInCalendarDays(competenceEnd, competenceStart) + 1;
    for (let year = competenceStart.getFullYear(); year < competenceEnd.getFullYear(); year++) {
      const dec31 = new Date(year, 11, 31);
      const daysAfter = differenceInCalendarDays(competenceEnd, dec31);
      // RISCONTO: defer the future slice (daysAfter).
      // RATEO: accrue the elapsed slice (totalDays - daysAfter).
      const amount = ensureDecimalPlaces(
        multiplyMoney(originalAmount, isRateo ? (totalDays - daysAfter) / totalDays : daysAfter / totalDays),
        2,
      );
      bookkeepingTransactions.push(
        ...transactionPair(
          dec31,
          amount,
          adjustmentAccountId,
          originalBookkeepingAccountId,
          mainDescription,
          stornoDescription,
          adjustmentType,
        ),
      );
    }
  }

  return { adjustmentType, sourceType, bookkeepingAccountId: adjustmentAccountId, bookkeepingTransactions };
}

/**
 * Derives the BookkeepingAdjustmentType from the registration context.
 * @param isExpense - true when the entry is in dare (cost), false when in avere (revenue)
 */
export function deriveAdjustmentType(
  registrationDate: Date,
  competenceStart: Date,
  competenceEnd: Date,
  isExpense: boolean,
): BookkeepingAdjustmentType | null {
  if (competenceStart.getFullYear() !== competenceEnd.getFullYear()) {
    if (registrationDate.getFullYear() <= competenceStart.getFullYear()) {
      return isExpense ? BookkeepingAdjustmentType.RISCONTO_ATTIVO : BookkeepingAdjustmentType.RISCONTO_PASSIVO;
    }
    return isExpense ? BookkeepingAdjustmentType.RATEO_PASSIVO : BookkeepingAdjustmentType.RATEO_ATTIVO;
  }
  if (registrationDate.getFullYear() > competenceEnd.getFullYear()) {
    return isExpense ? BookkeepingAdjustmentType.FATTURA_DA_RICEVERE : BookkeepingAdjustmentType.FATTURA_DA_EMETTERE;
  }
  return null;
}
